/** Selector for any instance of this _Friendation_ component */
const selector = '[is="block-dhl-retoure"]';

/**
 * Friendation component
 * Handles client-side validation for DHL Retoure forms
 */
export const blockDhlRetoure = () => {
  // Find all components on the page
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((component) => {
    const form = component.querySelector('form') as HTMLFormElement;
    const formContainer = component.querySelector(
      '.container'
    ) as HTMLDivElement;

    // Keep track of the first invalid field for focusing
    let firstInvalid: HTMLElement | null = null;

    /**
     * Validate a single field and show/remove the corresponding error message.
     * - Adds "invalid" CSS class when the field is invalid
     * - Appends an error message <li> to the errorBox
     * - Sets `aria-describedby` for accessibility
     */
    const handleFieldValidation = (
      field: HTMLInputElement | HTMLSelectElement | null,
      condition: boolean,
      msg: string,
      errorBox: HTMLUListElement
    ): boolean => {
      if (!field) return true;

      const errorId = `error-${field.id}`;
      let errorItem = errorBox.querySelector(`#${errorId}`) as HTMLLIElement;

      if (!condition) {
        field.classList.add('invalid');

        // Create error message if it doesn’t exist yet
        if (!errorItem) {
          errorItem = document.createElement('li');
          errorItem.id = errorId;
          errorItem.dataset.errorFor = field.id;
          errorItem.textContent = msg;
          errorBox.appendChild(errorItem);
        }

        // Associate field with error message
        field.setAttribute('aria-describedby', errorId);

        if (!firstInvalid) firstInvalid = field;
        return false;
      } else {
        field.classList.remove('invalid');

        // Remove error message and aria-describedby when fixed
        if (errorItem) errorItem.remove();
        if (field.hasAttribute('aria-describedby')) {
          field.removeAttribute('aria-describedby');
        }

        return true;
      }
    };

    /**
     * Validate the entire form.
     * - Loops through all required fields
     * - Creates or resets the error box
     * - Registers live validation for each field
     */
    const handleFormValidation = () => {
      firstInvalid = null; // reset before new validation

      // Create or reuse error list container
      let errorBox = formContainer.querySelector<HTMLUListElement>('.errors');
      if (!errorBox) {
        errorBox = document.createElement('ul');
        errorBox.className = 'errors';
        errorBox.setAttribute('role', 'alert');
        formContainer.prepend(errorBox);
      }
      errorBox.innerHTML = ''; // clear old errors

      // Form fields
      const knr = form.querySelector('#knr') as HTMLInputElement;
      const kname = form.querySelector('#kname') as HTMLInputElement;
      const router = form.querySelector('#router') as HTMLSelectElement;
      const datenschutz = form.querySelector(
        '#dhl_datenschutz'
      ) as HTMLInputElement;

      // Error messages (fallback if no data-error attribute is set)
      const errorMessages = {
        knr:
          (
            component.querySelector(
              '[is="atom-input"]:has(input#knr)'
            ) as HTMLElement | null
          )?.dataset.error || 'Bitte geben Sie Ihre Kundennummer an.',
        kname:
          (
            component.querySelector(
              '[is="atom-input"]:has(input#kname)'
            ) as HTMLElement | null
          )?.dataset.error || 'Bitte geben Sie Ihren Namen an.',
        router:
          (
            component.querySelector(
              '[is="atom-input"]:has(select#router)'
            ) as HTMLElement | null
          )?.dataset.error || 'Bitte wählen Sie einen gültigen Router.',
        datenschutz:
          (
            component.querySelector(
              '[is="atom-input"]:has(input#dhl_datenschutz)'
            ) as HTMLElement | null
          )?.dataset.error ||
          'Bitte akzeptieren Sie den Hinweis zum Datenschutz.'
      };

      // Run validation rules
      let valid = true;

      valid =
        handleFieldValidation(
          knr,
          !!knr && knr.value.trim() !== '',
          errorMessages.knr,
          errorBox
        ) && valid;

      valid =
        handleFieldValidation(
          kname,
          !!kname && kname.value.trim() !== '',
          errorMessages.kname,
          errorBox
        ) && valid;

      valid =
        handleFieldValidation(
          router,
          !!router && router.value !== '',
          errorMessages.router,
          errorBox
        ) && valid;

      valid =
        handleFieldValidation(
          datenschutz,
          !!datenschutz && datenschutz.checked,
          errorMessages.datenschutz,
          errorBox
        ) && valid;

      // Attach live validation (runs on input/change)
      [knr, kname, router, datenschutz].forEach((field) => {
        if (!field) return;
        const eventType =
          field instanceof HTMLSelectElement || field.type === 'checkbox'
            ? 'change'
            : 'input';

        field.addEventListener(eventType, () => {
          handleFieldValidation(
            field,
            field === knr || field === kname
              ? (field as HTMLInputElement).value.trim() !== ''
              : field === router
                ? router!.value !== ''
                : datenschutz!.checked,
            errorMessages[field.id as keyof typeof errorMessages],
            errorBox!
          );
        });
      });

      return valid;
    };

    /**
     * Intercept submit event
     * - Prevent submission if form is invalid
     * - Focus the first invalid field
     */
    form.addEventListener('submit', (e) => {
      if (!handleFormValidation()) {
        e.preventDefault();
        firstInvalid?.focus();
      }
    });
  });
};
